/**
 * Brand theming from integration.product_name
 * velvet  → luxury register: Cormorant Garamond, warm parchment
 * recon   → confidence register: Inter, crisp blues/neutrals
 * default → Cal neutral: white + slate, functional elegance
 */

export function getTheme(productName) {
  const p = (productName || '').toLowerCase();

  if (p === 'velvet') {
    return {
      brand: 'velvet',
      // Colors
      bg: '#F5F0E8',
      surface: '#FAF7F2',
      surfaceAlt: '#F0EBE0',
      border: '#E0D8C8',
      text: '#2C1810',
      textSecondary: '#6B5540',
      textMuted: '#A08060',
      accent: '#B8860B',
      accentHover: '#9A6E09',
      accentText: '#FFFFFF',
      pill: '#E8E0D0',
      pillBorder: '#C8B898',
      pillSelected: '#B8860B',
      pillSelectedText: '#FFFFFF',
      error: '#8B2500',
      success: '#2D5A27',
      // Typography
      fontDisplay: "'Cormorant Garamond', 'Georgia', serif",
      fontBody: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      fontMono: "'DM Mono', monospace",
      // Display sizes
      displaySize: '2.5rem',
      titleSize: '1.5rem',
      // Spacing feels generous, unhurried
      pagePadding: '40px 32px',
      sectionGap: '48px',
      // Animations
      transition: '0.3s ease',
      // Name
      label: 'Velvet',
    };
  }

  if (p === 'recon') {
    return {
      brand: 'recon',
      bg: '#F8FAFC',
      surface: '#FFFFFF',
      surfaceAlt: '#F1F5F9',
      border: '#E2E8F0',
      text: '#0F172A',
      textSecondary: '#334155',
      textMuted: '#64748B',
      accent: '#1D4ED8',
      accentHover: '#1E40AF',
      accentText: '#FFFFFF',
      pill: '#EFF6FF',
      pillBorder: '#BFDBFE',
      pillSelected: '#1D4ED8',
      pillSelectedText: '#FFFFFF',
      error: '#DC2626',
      success: '#15803D',
      fontDisplay: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontBody: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontMono: "'JetBrains Mono', monospace",
      displaySize: '2rem',
      titleSize: '1.375rem',
      pagePadding: '32px 24px',
      sectionGap: '32px',
      transition: '0.15s ease',
      label: 'Recon',
    };
  }

  // Cal neutral — warm parchment, confident execution
  // Research: warm off-white is a differentiator in scheduling (all majors ship white/cool).
  // UX literature: warm backgrounds signal approachability + trust. Risk is execution not strategy.
  // Fix: near-black text, white cards lifted off bg, ONE saturated accent (#D4622A terracotta), no gray soup.
  return {
    brand: 'cal',
    bg: '#F7F6F3',           // warm parchment — intentional, not unfinished
    surface: '#FFFFFF',       // white cards lifted off bg = depth
    surfaceAlt: '#EFECE6',
    border: '#E2DDD5',
    text: '#1A1A18',          // near-black — not pure black, not gray. reads warm.
    textSecondary: '#4A4843',
    textMuted: '#9C9890',
    accent: '#C9581F',        // terracotta — warm, saturated, decisive. not blue, not gray.
    accentHover: '#B04D1A',
    accentText: '#FFFFFF',
    pill: '#FFFFFF',          // white pills on parchment bg = crisp
    pillBorder: '#D8D3CB',
    pillSelected: '#1A1A18',  // near-black selected = confident
    pillSelectedText: '#FFFFFF',
    error: '#DC2626',
    success: '#16A34A',
    fontDisplay: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    fontUI: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    fontBody: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    fontMono: "'JetBrains Mono', 'Fira Code', monospace",
    displaySize: '2rem',
    titleSize: '1.375rem',
    pagePadding: '40px 24px',
    sectionGap: '32px',
    transition: '0.18s ease',
    label: 'Cal',
  };
}

export const NEUTRAL_THEME = getTheme(null);
